// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartElement.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartElement model
 */
!(function () {
  var m = require('mobx');
  var setValue = require('share/utils/Izenda.setValue');
  var ReportPartElementUtils = require('share/utils/Izenda.ReportPartElementUtils');
  var ReportPartElementProperties = require('./Izenda.ReportPartElementProperties');
  var ReportDesignerConstants = require('logic/data/Izenda.ReportDesignerConstants');
  var DATA_TYPES = ReportDesignerConstants.DATA_TYPES;
  var NO_SORT_DATA_TYPES = ReportDesignerConstants.NO_SORT_DATA_TYPES;
  var trackable = require('share/tracking/Izenda.Trackable');
  var FUNCTION = require('logic/data/Izenda.ReportPartFieldConstants').FUNCTION;
  var propertiesBuilderConstants = require('logic/data/Izenda.PropertiesBuilderConstants');
  const { isEmpty, flow, get, clone, includes, flatMap, trim, without, find } = require('lodash/fp');

  /**
   * function for building the relation data for the predicted field.
   */
  function getPredictionRelationData(fieldData, querySource) {
    return {
      relationData: {},
      fieldId: fieldData.id,
      fieldName: fieldData.name,
      fieldNameAlias: fieldData.originalName,
      dataFieldType: fieldData.izendaDataType,
      querySourceId: fieldData.querySourceId,
      querySourceType: querySource.isAlias ? null : querySource.type,
      sourceAlias: querySource.name,
      relationshipId: querySource.isAlias ? querySource.id : null,
      visible: true, // false
      calculatedTree: null,
      schemaName: querySource.querySourceCategoryName,
      querySourceName: querySource.name,
      databaseName: querySource.connectionName,
      isCalculated: fieldData.isCalculated,
      isRunningField: fieldData.isRunningField,
      hasAggregatedFunction: fieldData.hasAggregatedFunction,
      hasSupportDefaultTotal: fieldData.hasSupportDefaultTotal,
      isPredicated: fieldData.isPredicated,
      modelName: fieldData.modelName,
      relationColumn: fieldData.relationColumn
    }
  }

  /**
   * ReportPartElement
   * @param {field} field        Field of elements
   * @param {querySource} querySource querySource of elements
   * @param {string} name        name of elements
   * @param {int} position    position in container
   */
  function ReportPartElement(field, querySource, name, position, defaultSort) {
    field = field || {};
    querySource = querySource || {};
    this.reportPartContent = null;
    const isPredictedField = field.isPredicated;
    let relationData = {};
    if (isPredictedField) {
      const fieldData = find(item => {
        let filedName = field.relationColumn || '';
        let filterFieldName = item.name || ''
        return filedName.toLowerCase() === filterFieldName.toLowerCase()
      }, querySource.filterFields) || {};
      relationData = getPredictionRelationData(fieldData, querySource)
    }
    var props = {
      repeaterLevel: 0,
      name: name || '',
      properties: new ReportPartElementProperties(defaultSort),
      position: position || 0,
      field: {
        relationData: field.relationData ? field.relationData : relationData,
        fieldId: field.id,
        fieldName: field.name,
        fieldNameAlias: name,
        dataFieldType: field.izendaDataType,
        querySourceId: field.querySourceId,
        querySourceType: querySource.isAlias ? null : querySource.type,
        sourceAlias: querySource.name,
        relationshipId: querySource.isAlias ? querySource.id : null,
        visible: true, // false
        calculatedTree: null,
        schemaName: querySource.querySourceCategoryName,
        querySourceName: querySource.name,
        databaseName: querySource.connectionName,
        isCalculated: field.isCalculated,
        isRunningField: field.isRunningField,
        hasAggregatedFunction: field.hasAggregatedFunction,
        hasSupportDefaultTotal: field.hasSupportDefaultTotal,
        isPredicated: field.isPredicated,
        modelName: field.modelName,
        relationColumn: field.relationColumn
      },

      embededInfo: function () {
        var subReport = get('drillDown.subReport', this.properties);
        var selectedReport = get('selectedReport', subReport);
        var reportPartUsed = get('reportPartUsed', subReport);
        if (subReport.style !== ReportDesignerConstants.DRILL_DOWN_STYLE_OPTION_VALUES.embedded) {
          return {
            reportId: null,
            reportPartIds: []
          };
        }
        return {
          reportId: reportPartUsed === 'All' ? selectedReport : null,
          reportPartIds: reportPartUsed !== 'All' ? reportPartUsed.split(',') : []
        };
      },

      haveFunction: function () {
        return this.field.hasAggregatedFunction || !!this.properties.dataFormattings.function;
      },

      fieldId: function () {
        return this.field.fieldId;
      },

      relationData: function () {
        return this.field.relationData;
      },

      fieldFullName: function () {
        return String.format(
          '[{0}].[{1}].[{2}].[{3}]',
          this.field.databaseName,
          this.field.schemaName,
          this.field.querySourceName,
          this.field.fieldName
        );
      },

      fieldName: function () {
        return this.field.fieldNameAlias || this.field.fieldName || this.name;
      },

      fieldNameAlias: function () {
        return this.fieldName;
      },
      isDeleted: false,
      isSelected: false,

      fieldFormatData: function () {
        var properties = this.properties;
        var fieldFormat = properties.getValueByKey && properties.getValueByKey('format');

        return fieldFormat && fieldFormat.format;
      },

      previewDataType: function () {
        return this.previewDataTypeByFunction;
      },

      isDateTimeType: function () {
        return this.fieldDataType === ReportDesignerConstants.DATA_TYPES.DATE_TIME;
      },

      dateTimeFormatData: m.asStructure(function () {
        return ReportPartElementUtils.getDateTimeFormatData(
          get('field.dataFieldType', this),
          this.properties.getDataValueByKey('functionInfo'),
          this.properties.getValueByKey('format')
        );
      }),

      previewDataTypeByFunction: function () {
        return ReportPartElementUtils.previewDataTypeByFunction(
          this.properties.getDataValueByKey('functionInfo') || {},
          get('field.dataFieldType', this)
        );
      },

      isVisible: function () {
        return get('field.visible', this) && get('properties.fieldItemVisible', this);
      },

      isNumericPreviewData: function () {
        return [DATA_TYPES.NUMERIC, DATA_TYPES.MONEY].indexOf(this.previewDataType) > -1;
      },

      hasSubReport: function () {
        return !!get('drillDown.subReport.selectedReport', this.properties);
      },

      hasSubTotal: function () {
        return !isEmpty(this.properties.dataFormattings.subTotal.label);
      },

      fieldDataType: function () {
        return this.field && this.field.dataFieldType;
      },

      hiddenSortFeature: function () {
        return includes(this.previewDataType, NO_SORT_DATA_TYPES);
      },

      numberOfPointActions: function () {
        var properties = this.properties;
        var subReport = properties.getValueByKey('subReport');
        var customURL = properties.getValueByKey('customURL');
        var embeddedJavascript = properties.getValueByKey('embeddedJavascript');
        var count = 0;
        subReport && subReport.selectedReport && count++;
        customURL && customURL.url && count++;
        embeddedJavascript && embeddedJavascript.script && count++;
        return count;
      },

      isAggregatedField: function () {
        var functionInfo = this.functionInfo;
        return (
          !this.isDeleted &&
          !isEmpty(functionInfo) &&
          !!functionInfo.name &&
          functionInfo.name !== FUNCTION.GROUP.NAME &&
          functionInfo.name !== FUNCTION.GROUP_DAYS_OLD.NAME
        );
      },
      functionInfo: function () {
        var properies = this.properties || {};
        var dataFormattings = properies.dataFormattings || {};
        var functionInfo = dataFormattings.functionInfo || {};

        return functionInfo;
      }
    };

    m.extendObservable(trackable(this), props);

    m.autorun(
      function () {
        return this.hiddenSortFeature && this.properties.setSortValue('');
      }.bind(this)
    );

    this.cleanDirty();
  }

  ReportPartElement.prototype.getValueByKey = function (key) {
    return !this.isDeleted ? this[key] || this.field[key] || this.properties.getValueByKey(key) : undefined;
  };

  ReportPartElement.prototype.delete = m.action(function () {
    this.isDeleted = true;
  });

  ReportPartElement.prototype.resetProperties = m.action(function (defaultSort) {
    this.properties = new ReportPartElementProperties(defaultSort);
  });

  /**
   * read data from api to populate data for this object
   * @param  {Object} data [elements JSON from api]
   * @return {ReportPartElement}      [this object]
   */
  ReportPartElement.prototype.readData = m.action(function (data) {
    setValue(this, 'name', data.name);
    setValue(this, 'position', data.position);

    var field = this.field;
    var fieldData = data.field || {};

    setValue(field, 'relationData', fieldData.relationData);
    setValue(field, 'querySourceId', fieldData.querySourceId);
    setValue(field, 'fieldId', fieldData.fieldId);
    setValue(field, 'fieldName', fieldData.fieldName);
    setValue(field, 'fieldNameAlias', fieldData.fieldNameAlias);
    setValue(field, 'dataFieldType', fieldData.dataFieldType);
    setValue(field, 'visible', fieldData.visible);
    setValue(field, 'schemaName', fieldData.schemaName);
    setValue(field, 'querySourceName', fieldData.querySourceName);
    setValue(field, 'databaseName', fieldData.databaseName);
    setValue(field, 'calculatedTree', fieldData.calculatedTree);
    setValue(field, 'isCalculated', fieldData.isCalculated);
    setValue(field, 'querySourceType', fieldData.querySourceType);
    setValue(field, 'sourceAlias', fieldData.sourceAlias);
    setValue(field, 'relationshipId', fieldData.relationshipId);
    setValue(field, 'hasAggregatedFunction', fieldData.hasAggregatedFunction);
    setValue(field, 'hasSupportDefaultTotal', fieldData.hasSupportDefaultTotal);
    setValue(field, 'isRunningField', fieldData.isRunningField);
    setValue(field, 'isPredicated', fieldData.isPredicated);
    setValue(field, 'modelName', fieldData.modelName);
    setValue(field, 'relationColumn', fieldData.relationColumn);
    this.properties.updateData(data.properties);
    this.cleanDirty();
    return this;
  });

  /**
   * check drity status of this elements
   * @return {boolean} dirty status
   */
  ReportPartElement.prototype.checkDirty = function () {
    return this.isDirty || (this.properties.checkDirty && this.properties.checkDirty());
  };

  /**
   * Clean dirty status.
   */
  ReportPartElement.prototype.cleanDirty = m.action(function () {
    this.properties.resetDirty && this.properties.resetDirty();

    this.resetDirty();
  });

  /**
   * Update data of field in report parts and its properties
   * @param  {[type]} fieldInfo       [description]
   * @param  {[type]} propertiesInfo  [description]
   * @param  {[type]} querySourceInfo [description]
   * @return {[type]}                 [description]
   */
  ReportPartElement.prototype.updateData = m.action(function (fieldInfo, propertiesInfo, querySourceInfo) {
    var field = this.field;

    fieldInfo = fieldInfo || {};
    querySourceInfo = querySourceInfo || {};

    this.setName(fieldInfo.fieldNameAlias);

    setValue(field, 'querySouceId', querySourceInfo.querySouceId);

    setValue(field, 'fieldId', fieldInfo.fieldId);
    setValue(field, 'fieldName', fieldInfo.fieldName);
    setValue(field, 'dataFieldType', fieldInfo.dataFieldType);
    setValue(field, 'calculatedTree', fieldInfo.calculatedTree);
    setValue(field, 'isCalculated', fieldInfo.isCalculated);
    setValue(field, 'querySourceType', fieldInfo.querySourceType);
    setValue(field, 'sourceAlias', fieldInfo.sourceAlias);
    setValue(field, 'relationshipId', fieldInfo.relationshipId);
    setValue(field, 'isPredicated', fieldInfo.isPredicated);
    setValue(field, 'modelName', fieldInfo.modelName);
    setValue(field, 'relationColumn', fieldInfo.relationColumn);

    this.setProperties(propertiesInfo);

    this.markDirty();
  });

  /**
   * Set properties of field
   * @param  {object} propertiesInfo
   * @return undefined
   */
  ReportPartElement.prototype.setProperties = m.action(function (propertiesInfo) {
    // update field properties
    this.properties.updateData(propertiesInfo || {});
    this.markDirty();
  });

  /**
   * Set other properties
   */
  ReportPartElement.prototype.setOtherProps = m.action(function (key, value) {
    // update field properties
    this.properties.setOtherProps(key, value);
    this.markDirty();
  });

  /**
   * [setUniqueName Set Unique name for field]
   * @method setUniqueName
   * @param  {string}      name             Base string
   * @param  {ReportPartDetail}      reportPartDetail ReportPartDetail
   */
  ReportPartElement.prototype.setUniqueName = m.action(function (name, reportPartDetail) {
    var elements = flow(
      flatMap(function (container) {
        return container.elements ? container.elements.peek() : [];
      }),
      without([this])
    )(reportPartDetail.containerCollection);
    var newNameAlias = String.generateNewName(name, elements, get('name'), '()');

    this.setName(newNameAlias);
  });

  /**
   * set Name for field
   * @method setName
   * @param  {string} name New name for element
   */
  ReportPartElement.prototype.setName = m.action(function (name) {
    this.field.fieldNameAlias = this.name = trim(name);
  });

  /**
   * set format for field
   * @method setFormat
   * @param  {string} data                        Function Data and format data
   * @param  {ReportPartDetails} reportPartDetail Root Object of current report Part
   */
  ReportPartElement.prototype.setFormat = m.action(function (data) {
    data.formatData &&
      this.properties.setFormat({
        formatId: data.formatData.id,
        format: data.formatData.format,
        groupBy: data.formatData.groupBy
      });
  });

  /**
   * set function and format for field
   * @method setFunctionFormat
   * @param  {string} data                        Function Data and format data
   * @param  {ReportPartDetails} reportPartDetail Root Object of current report Part
   */
  ReportPartElement.prototype.setFunctionFormat = m.action(function (data, reportPartDetail) {
    data.formatData &&
      this.properties.setFormat({
        formatId: data.formatData.id,
        format: data.formatData.format,
        groupBy: data.formatData.groupBy
      });
    return this.setFieldFunction(data.functionData, reportPartDetail);
  });

  ReportPartElement.prototype.setFunction = function (data) {
    data.functionData && this.properties.setFunction(data.functionData);
  };

  ReportPartElement.prototype.setSelected = function (value) {
    this.isSelected = value;
  };

  /**
   * Reset default alternative text setting to default value
   */
  ReportPartElement.prototype.resetDefaultAlternativeText = m.action('resetDefaultAlternativeText', function () {
    this.properties.dataFormattings.alternativeText = clone(
      propertiesBuilderConstants.DEFAULT_PROPERTIES_VALUE.dataFormattings.alternativeText
    );
    this.markDirty();
  });

  /**
   * Reset default custom url setting to default value
   */
  ReportPartElement.prototype.resetDefaultCustomURL = m.action('resetDefaultCustomURL', function () {
    this.properties.dataFormattings.customURL = clone(
      propertiesBuilderConstants.DEFAULT_PROPERTIES_VALUE.dataFormattings.customURL
    );
    this.markDirty();
  });

  /**
   * Reset default sub report setting to default value
   */
  ReportPartElement.prototype.resetDefaultSubReport = m.action('resetDefaultSubReport', function () {
    this.properties.drillDown.subReport = clone(
      propertiesBuilderConstants.DEFAULT_PROPERTIES_VALUE.drillDown.subReport
    );
    this.markDirty();
  });

  /**
   * Reset default embedded javascript setting to default value
   */
  ReportPartElement.prototype.resetDefaultEmbeddedJavascript = m.action('resetDefaultEmbeddedJavascript', function () {
    this.properties.dataFormattings.embeddedJavascript = clone(
      propertiesBuilderConstants.DEFAULT_PROPERTIES_VALUE.dataFormattings.embeddedJavascript
    );
    this.markDirty();
  });

  /**
   * Reset default sub total setting to default value
   */
  ReportPartElement.prototype.resetDefaultSubTotal = m.action('resetDefaultSubTotal', function () {
    this.properties.dataFormattings.subTotal = clone(
      propertiesBuilderConstants.DEFAULT_PROPERTIES_VALUE.dataFormattings.subTotal
    );
    this.markDirty();
  });

  /**
   * Reset default alternative text setting to default value
   */
  ReportPartElement.prototype.resetDefaultGrandTotal = m.action('resetDefaultGrandTotal', function () {
    this.properties.dataFormattings.grandTotal = clone(
      propertiesBuilderConstants.DEFAULT_PROPERTIES_VALUE.dataFormattings.grandTotal
    );
    this.markDirty();
  });

  /**
   * Reset default alternative text setting to default value
   */
  ReportPartElement.prototype.resetTextDefaultValue = m.action('resetTextDefaultValue', function () {
    this.properties.dataFormattings.color.textColor = {};
    this.markDirty();
  });

  /**
   * Reset default alternative text setting to default value
   */
  ReportPartElement.prototype.resetCellDefaultValue = m.action('resetCellDefaultValue', function () {
    this.properties.dataFormattings.color.cellColor = {};
    this.markDirty();
  });

  /*
   * Update column width
   * columnWidth {number}
   */
  ReportPartElement.prototype.updateColumnWidth = m.action(function (columnWidth) {
    if (columnWidth) {
      this.properties.dataFormattings.width.value = columnWidth;
      this.markDirty();
    }
  });

  ReportPartElement.prototype.clone = function () {
    var cloned = new ReportPartElement();
    return cloned.readData(this);
  };

  ReportPartElement.prototype.setRepeaterLevel = function (value) {
    this.repeaterLevel = value;
  }

  module.exports = ReportPartElement;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartElement.js